import apiClient from "utils/apiClient";
import { O_API_KEY, O_CLIENT_APP } from "constants";

const CLIENT_ID_KEY = 'clientId';
const CLIENT_VALIDATED_KEY = 'clientValidated';

export async function getClientId() {
  try {
    return localStorage.getItem(CLIENT_ID_KEY);
  } catch (e) {
    return null;
  }
}

export async function setClientId(clientId) {
  try {
    localStorage.setItem(CLIENT_ID_KEY, clientId);
  } catch (e) {
    throw e;
  }
}

export async function isClientValidated() {
  try {
    const validated = localStorage.getItem(CLIENT_VALIDATED_KEY);
    return validated === 'true';
  } catch (e) {
    return false;
  }
}

export async function setClientValidated(validated) {
  try {
    localStorage.setItem(CLIENT_VALIDATED_KEY, validated.toString());
  } catch (e) {
    throw e;
  }
}

async function requestClientIdFromGateway() {
  try {
    const requestResp = await apiClient.post(
      `/api/v1/clients/register/request`,
      {},
      {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
        }
      }
    );

    if (!requestResp?.data?.id) {
      throw new Error('Client registration failed');
    }

    const clientIdFromGateway = requestResp.data.id;

    const confirmResp = await apiClient.put(
      `/api/v1/clients/register/confirm`,
      {},
      {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
          "O-CLIENT-ID": clientIdFromGateway,
        }
      }
    );

    if (confirmResp?.data?.id !== clientIdFromGateway) {
      throw new Error('Client confirmation failed');
    }

    return clientIdFromGateway;
  } catch (error) {
    if (error.response) {
      console.error('Response Status:', error.response.status);
      console.error('Response Data:', error.response.data);
    }
    throw error;
  }
}

async function validateClientWithGateway(clientId) {
  try {
    const response = await apiClient.get(`/api/v1/clients/validate`, {
      headers: {
        "O-API-KEY": O_API_KEY,
        "O-CLIENT-APP": O_CLIENT_APP,
        "O-CLIENT-ID": clientId,
      }
    });

    return Boolean(response?.data?.id);
  } catch (error) {
    return false;
  }
}

export async function ensureClientId() {
  try {
    const existingClientId = await getClientId();
    const isValidated = await isClientValidated();

    if (existingClientId && isValidated) {
      return existingClientId;
    }

    if (existingClientId && !isValidated) {
      const isValid = await validateClientWithGateway(existingClientId);

      if (isValid) {
        await setClientValidated(true);
        return existingClientId;
      } else {
        localStorage.removeItem(CLIENT_ID_KEY);
        localStorage.removeItem(CLIENT_VALIDATED_KEY);
      }
    }

    const clientId = await requestClientIdFromGateway();

    if (clientId) {
      await setClientId(clientId);
      await setClientValidated(true);
      return clientId;
    }

    return null;
  } catch (error) {
    console.error('Client registration failed:', error);
    return null;
  }
}

export async function validateClientId() {
  try {
    const clientId = await getClientId();
    if (!clientId) {
      return false;
    }

    const isValid = await validateClientWithGateway(clientId);

    if (isValid) {
      await setClientValidated(true);
    }

    return isValid;
  } catch (error) {
    return false;
  }
}

export async function clearClientData() {
  try {
    localStorage.removeItem(CLIENT_ID_KEY);
    localStorage.removeItem(CLIENT_VALIDATED_KEY);
  } catch (error) {
    console.error('Failed to clear client data:', error);
  }
}